import { BOOKINGHTTP, HTTP, HTTP_CONFIG } from '../../common/http';

import {
    CLEAR_QUOTE,
    CLEAR_VEHICLES,
    CREATE_QUOTE,
    DELETE_VEHICLE,
    FETCH_BOOKING,
    FETCH_CURRENCIES,
    FETCH_DONATIONS,
    FETCH_EXTRAS,
    FETCH_LOCATIONS,
    FETCH_MODIFICATION,
    FETCH_LAST_MODIFICATION,
    FETCH_RENTAL_CODE,
    FETCH_MODIFICATION_EXTRAS,
    FETCH_GUEST_BOOKING,
    FETCH_SELECTED_EXTRAS,
    FETCH_TOTALS,
    FETCH_LOYALTY_LEVELS,
    FETCH_VEHICLE_INSURANCE_OPTIONS,
    FETCH_VEHICLES,
    APPEND_VEHICLES,
    SET_VEHICLES_TOTAL,
    SET_VEHICLES_REQUEST_TOKEN,
    SET_PAGINATION_DATA,
    SET_VEHICLE_FACETS,
    SET_FACETS_REQUEST_TOKEN,
    SET_CURRENCY,
    SET_CURRENCY_CHANGE_TOKEN,
    REGISTER_VEHICLE_LISTING,
    UNREGISTER_VEHICLE_LISTING,
    SET_CUSTOMER,
    SET_CUSTOMER_STATE,
    SET_DROPOFF_DATE,
    SET_DROPOFF_LOCATION,
    SET_DROPOFF_TIME,
    SET_GRAND_TOTAL,
    SET_LOADING,
    SET_PICKUP_DATE,
    SET_PICKUP_LOCATION,
    SET_PICKUP_TIME,
    SET_SELECTED_VEHICLE_TYPE,
    TOGGLE_PREMIUM_VEHICLE_UPGRADE,
    UPDATED_BOOKING,
    UPDATE_PAYMENT_TYPE,
    UPDATE_DONATION,
    SET_TAB,
    SET_CURRENCY_REFRESHING,
} from './mutation-types';
import { SET_ORIGINAL_QUOTE, SET_ORIGINAL_DONATION } from '../edit-booking/mutation-types';
import { broadcastCurrencyChange } from './currencySync';

const localDomain = window.greenmotion ? window.greenmotion.portalEndpoint || '' : '';

// Tracks a setCurrencyState() call still in flight, so a second call for the same
// currency arriving before the first one's commit lands (e.g. Layout.vue's Kayak init
// calling selectCurrency() and then dispatching setCurrencyState() back-to-back) reuses
// that same promise instead of re-posting to the API and re-emitting the currency-change
// events a second time.
let pendingCurrencyChange = null;

export function setLoading({ commit }, loading) {
    commit(SET_LOADING, loading);
}

export function fetchLocations({ commit }) {
    return HTTP.get(`locations?include=country`).then((response) => commit(FETCH_LOCATIONS, response.data.data));
}

export function fetchCurrencies({ commit, state }) {
    return HTTP.get(`currencies`).then((response) => commit(FETCH_CURRENCIES, response.data.data));
}

export function fetchQuote({ commit, dispatch, state }, quoteId) {
    const currencyToken = state.currencyChangeToken;

    return HTTP.get(`quotes/${quoteId}`, {
        params: {
            include:
                'selected_vehicle.vehicle,selected_vehicle.product,selected_vehicle.vehicle.product,selected_vehicle.vehicle.products,selected_vehicle.selected_insurance_options.insurance_option,selected_extras.extra,totals,user.loyalty_level,user.cards,customer,signed_routes',
        },
    }).then((response) => {
        // The currency changed while this was in flight, so its totals are in the old
        // currency and CREATE_QUOTE would restore the old state.currency. Refetch instead -
        // this also covers a change made before the quote had an ID, which
        // applyCurrencyChange can't refresh itself.
        if (state.currencyChangeToken !== currencyToken) {
            return dispatch('fetchQuote', quoteId);
        }

        commit(CREATE_QUOTE, response.data.data);
        return response;
    });
}

export function fetchOriginalQuote({ commit, state }, { bookingReference, quoteId }) {
    return new Promise((resolve, reject) => {
        return HTTP.get(`bookings/${bookingReference}/quote/${quoteId}`, {
            params: {
                include:
                    'pickup_location,dropoff_location,selected_vehicle.vehicle,selected_vehicle.product,selected_vehicle.vehicle.product,selected_vehicle.vehicle.products,selected_vehicle.selected_insurance_options.insurance_option,selected_extras.extra,totals,user.loyalty_level,user.cards,customer,signed_routes',
            },
        }).then((response) => {
            commit(SET_ORIGINAL_QUOTE, response.data.data);
            resolve(response);
        });
    });
}

export function fetchBookingQuote({ commit, state }, { bookingReference, quoteId }) {
    return new Promise((resolve, reject) => {
        return HTTP.get(`bookings/${bookingReference}/quote/${quoteId}`, {
            params: {
                include:
                    'pickup_location,dropoff_location,selected_vehicle.vehicle,selected_vehicle.product,selected_vehicle.vehicle.product,selected_vehicle.vehicle.products,selected_vehicle.selected_insurance_options.insurance_option,selected_extras.extra,totals,user.loyalty_level,user.cards,customer,signed_routes',
            },
        }).then((response) => {
            commit(CREATE_QUOTE, response.data.data);
            resolve(response);
        });
    });
}

export function setQuote({ commit, state }, quote) {
    commit(CREATE_QUOTE, quote);
}

export function fetchBooking({ commit, state }, bookingId) {
    return new Promise((resolve, reject) => {
        HTTP.get(`bookings/${bookingId}/json`)
            .then((response) => {
                const booking = response?.data?.data;

                commit(FETCH_BOOKING, response?.data?.data);
                localStorage.setItem(
                    'booking',
                    JSON.stringify({
                        bookingReference: response?.data?.data?.reference,
                        complete: true,
                    })
                );

                resolve(response);
            })
            .catch((error) => console.log(error));
    });
}

export function fetchBookingNoCommit({ commit, state }, bookingId) {
    return new Promise((resolve, reject) => {
        HTTP.get(`bookings/${bookingId}/json`)
            .then((response) => {
                resolve(response);
                return response;
            })
            .catch((error) => console.log(error));
    });
}

export function setUpdateBooking({ commit, state }, value) {
    commit(UPDATED_BOOKING, value);
}

export function clearQuote({ commit }) {
    commit(CLEAR_QUOTE);
}

export function fetchQuoteWithAdditionalIncludes({ commit, state }, { quoteId, additionalIncludes }) {
    return HTTP.get(`quotes/${quoteId}`, {
        params: {
            include: `selected_vehicle.vehicle,selected_vehicle,selected_vehicle.vehicle.product,selected_vehicle.product,selected_vehicle.vehicle.products,selected_vehicle.selected_insurance_options.insurance_option,selected_extras.extra,totals,user.loyalty_level,user.cards,customer,${additionalIncludes}`,
        },
    }).then((response) => {
        commit(CREATE_QUOTE, response.data.data);
    });
}

export function fetchVehicles({ commit, state }, { quoteId, sortDriveAndGo, sortDirection, limit, page, filters = {} } = {}) {
     // Only apply pagination when provided; omitting limit/page preserves the legacy "all vehicles" behavior for existing callers.
     const params = { include: 'products', ...filters };

    if (limit != null) params.limit = limit;
    if (page != null) params.page = page;

    if (sortDriveAndGo) {
        // Backend only checks for the param's presence (request()->has('sort_drive_and_go')),
        // so any value works here - it's a sort flag, not a filter value.
        params.sort_drive_and_go = '';
    }

    if (sortDirection) params.sort_direction = sortDirection;

    // This is a replace (not append) request - bump the token so any fetchMoreVehicles
    // still in flight from before this call knows to discard its response instead of
    // appending stale vehicles onto the list this call is about to set.
    commit(SET_VEHICLES_REQUEST_TOKEN, state.vehiclesRequestToken + 1);
    const requestToken = state.vehiclesRequestToken;

    return HTTP.get(`quotes/${quoteId}/vehicles`, { params }).then((response) => {
        // A newer replace call has since started - let it win instead of overwriting
        // the list it's about to load with this stale response. Mark it so callers
        // that act on response.data directly (not just the committed store state)
        // can detect and discard it too, instead of acting on superseded data.
        if (state.vehiclesRequestToken !== requestToken) {
            response.stale = true;
            return response;
        }

        commit(FETCH_VEHICLES, response.data.data);
        commit(SET_VEHICLES_TOTAL, response.data.meta?.pagination?.total ?? response.data.data.length);

        const pagination = response.data.meta?.pagination;
        commit(SET_PAGINATION_DATA,
            pagination
                ? {
                      per_page: pagination.per_page,
                      current_page: pagination.current_page,
                      total_pages: pagination.total_pages,
                  }
                : null
        );

        return response;
    }).catch((error) => {
        // A superseded replace request rejecting (network error, aborted, etc.) shouldn't
        // fail the operation - only the current request's own failure should propagate.
        if (state.vehiclesRequestToken !== requestToken) {
            return { stale: true };
        }
        throw error;
    });
}

export function fetchMoreVehicles({ commit, state }, { quoteId, sortDriveAndGo, sortDirection, limit, page, filters = {} } = {}) {
    limit = limit ?? 1;
    page = page ?? 1;

    const params = { include: 'products', limit, page, ...filters };

    if (sortDriveAndGo) {
        params.sort_drive_and_go = '';
    }

    if (sortDirection) params.sort_direction = sortDirection;

    // Capture the token at fire time - if a fetchVehicles (filter/sort/currency change)
    // replaces the list before this page comes back, the token will have moved on and
    // this append is discarded below instead of corrupting the newly-replaced list.
    const requestToken = state.vehiclesRequestToken;

    return HTTP.get(`quotes/${quoteId}/vehicles`, { params }).then((response) => {
        if (state.vehiclesRequestToken !== requestToken) {
            return response;
        }

        commit(APPEND_VEHICLES, response.data.data);
        commit(SET_VEHICLES_TOTAL, response.data.meta?.pagination?.total ?? state.vehicles.length);

        const pagination = response.data.meta?.pagination;
        commit(SET_PAGINATION_DATA,
            pagination
                ? {
                      per_page: pagination.per_page,
                      current_page: pagination.current_page,
                      total_pages: pagination.total_pages,
                  }
                : null
        );

        return response;
    }).catch((error) => {
        // A superseded append request rejecting shouldn't fail the operation - the
        // reload that superseded it already owns the page state at this point.
        if (state.vehiclesRequestToken !== requestToken) {
            return { stale: true };
        }
        throw error;
    });
}

export function fetchVehicleFacets({ commit, state }, { quoteId } = {}) {
    // Facets are re-requested on every currency change, so an earlier request (the
    // initial load, or a prior currency change) can still be in flight when a newer
    // one starts. Bump/capture a token, same as fetchVehicles, so only the most
    // recently fired request's response is committed.
    commit(SET_FACETS_REQUEST_TOKEN, state.facetsRequestToken + 1);
    const requestToken = state.facetsRequestToken;

    return HTTP.get(`quotes/${quoteId}/vehicles/facets`).then((response) => {
        if (state.facetsRequestToken !== requestToken) {
            return response;
        }

        commit(SET_VEHICLE_FACETS, response.data.data);
        return response;
    }).catch((error) => {
        // A superseded facets request rejecting shouldn't fail the operation - only the
        // current request's own failure should reach fetchFacets().catch(...).
        if (state.facetsRequestToken !== requestToken) {
            return { stale: true };
        }
        throw error;
    });
}

export function clearVehicles({ commit }) {
    commit(CLEAR_VEHICLES);
}

export function fetchVehicleInsuranceOptions({ commit, state }, payload) {
    return HTTP.get(`quotes/${payload.quoteId}/vehicles/${payload.vehicleId}/insurance-options`).then((response) => commit(FETCH_VEHICLE_INSURANCE_OPTIONS, response.data.data));
}

export function copyExtras({ commit, state }, { quoteId, newQuoteId }) {
    quoteId = quoteId ? quoteId : state.quote.id;
    return HTTP.get(`quotes/${quoteId}/extras?include=profileCountries`).then((response) => commit(FETCH_EXTRAS, response.data.data));
}

export function fetchExtras({ commit, state }, quoteId) {
    quoteId = quoteId ? quoteId : state.quote.id;
    return HTTP.get(`quotes/${quoteId}/extras?include=profileCountries`).then((response) => {
        commit(FETCH_EXTRAS, response.data.data);
        return response;
    });
}

export function copyUpdateExtras({ commit, state }, { quoteId, originalId }) {
    return HTTP.get(`quotes/${quoteId}/selected-extras/quote/${originalId}/copy`).then((response) => {
        commit(FETCH_SELECTED_EXTRAS, response.data.data);
        return response;
    });
}

export function createQuote({ commit }, quote) {
    commit(CREATE_QUOTE, quote);
}

export function copyUpdateCustomer({ dispatch, commit, state }, { originalId }) {
    const quoteId = typeof state.quote !== 'undefined' && typeof state.quote?.id !== 'undefined' && state.quote?.id ? state.quote?.id : originalId;
    const data = new FormData();
    data.append('original_id', originalId);
    data.append('quote_id', quoteId);

    return HTTP.post(`quotes/${quoteId}/customers/copy`, data).then((response) => {
        commit(SET_CUSTOMER, response?.data);
        return response;
    });
}

export function createQuoteCustomer({ dispatch, state }, payload) {
    return HTTP.post(`quotes/${state.quote.id}/customers`, payload).then((response) =>
        dispatch('fetchQuote', state.quote.id).then(() => response)
    );
}

export function registerUserDuringPurchase({ dispatch }, payload) {
    return BOOKINGHTTP.post(`user/ajax-create`, payload).then((response) => {
        window.greenmotion.authenticated = !!response.data.success;

        if (response.data.success) {
            return dispatch('fetchOrRefreshToken').then(() => response);
        }

        return response;
    });
}

export function updateQuoteCustomerFull({ commit, state }, { payload, reference }) {
    return BOOKINGHTTP.post(`user/bookings/${reference}/modify-booking/details`, payload)
        .then((response) => {
            return response;
        })
        .catch((error) => console.log(error));
}

export function updateQuoteCreate({ commit, state }, { bookingId, quoteId, status = null }) {
    let address = `modify-reservation/${bookingId}/update/${quoteId}/pivot`;
    if (status) {
        address = `modify-reservation/${bookingId}/update/${quoteId}/pivot/${status}`;
    }

    return new Promise((resolve, reject) => {
        return HTTP.get(address)
            .then((response) => {
                resolve(response);
                return response;
            })
            .catch((error) => console.log(error));
    });
}

export function updateQuoteCustomer({ dispatch, state }, { payload, id }) {
    return HTTP.patch(`quotes/${state.quote.id}/customers/${id}`, payload).then((response) =>
        dispatch('fetchQuote', state.quote?.id).then(() => response)
    );
}

export function updateQuotePaymentType({ dispatch, state }, payload) {
    return HTTP.post(`quotes/${payload.quoteId}/update-payment-type`, {
        paymentType: payload.paymentType,
    }).then((response) => {
        dispatch('fetchQuote', state.quote?.id);

        return response;
    });
}

export function createVehicle({ commit, dispatch, state }, { selectedVehicle, productId, quoteId = state.quote.id }) {
    return HTTP.post(`quotes/${quoteId}/selected-vehicles`, {
        id: selectedVehicle.id,
        quote_vehicle_product_id: productId,
    }).then((response) => {
        return dispatch('fetchQuoteWithAdditionalIncludes', {
            quoteId: state.quote.id,
            additionalIncludes: 'selected_vehicle.basic_vehicle,selected_vehicle.premium_vehicle',
        }).then(() => response);
    });
}

export function assignDefaultVehicle({ commit, dispatch, state }, { selectedVehicle, productId, quoteId = state.quote.id }) {
    return HTTP.post(`quotes/${quoteId}/update/selected-vehicles`, {
        id: selectedVehicle.id,
        quote_vehicle_product_id: productId,
    }).then((response) => {
        dispatch('fetchQuoteWithAdditionalIncludes', {
            quoteId: state.quote.id,
            additionalIncludes: 'selected_vehicle.basic_vehicle,selected_vehicle.premium_vehicle',
        });
        return response;
    });
}

export function modifyBookingVehicle({ commit, dispatch, state }, { selectedVehicle, productId, quoteId, bookingId, bookingReference, useFreeProductUpgrade = null }) {
    return new Promise((resolve, reject) => {
        let payload = {
            id: selectedVehicle?.id,
            quote_vehicle_product_id: productId,
            booking_reference: bookingReference,
        };

        if (useFreeProductUpgrade !== null) {
            payload.use_free_product_upgrade = useFreeProductUpgrade;
        }

        return HTTP.post(`quotes/${quoteId}/modify/selected-vehicles/${bookingId}`, payload).then((response) => {
            commit(FETCH_MODIFICATION, response?.data?.data[0]);
            commit(FETCH_RENTAL_CODE, response?.data?.data[0]?.rental_code);
            resolve(response);
            return response;
        });
    });
}

export function createPartialModification({ commit, dispatch, state }, { bookingId, quoteId, useFreeProductUpgrade = null }) {
    return new Promise((resolve, reject) => {
        let payload = {};

        if (useFreeProductUpgrade !== null) {
            payload.use_free_product_upgrade = useFreeProductUpgrade;
        }

        return HTTP.post(`quotes/${quoteId}/start-partial-modification/${bookingId}`, payload).then((response) => {
            commit(FETCH_MODIFICATION, response?.data?.data[0]);
            commit(FETCH_RENTAL_CODE, response?.data?.data[0]?.rental_code);
            resolve(response);
            return response;
        });
    });
}

export function createQuoteForVehicleSelection({ commit, dispatch, state }, { quoteId, data = {} }) {
    return new Promise((resolve, reject) => {
        return HTTP.post(`quotes/${quoteId}/update/create-quote-update`, data)
            .then((response) => {
                resolve(response);
                return response;
            })
            .catch((error) => {
                reject(error);
            });
    });
}

export function deleteVehicle({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
        return HTTP.delete(`quotes/${state.quote.id}/selected-vehicles/${state.quote.selected_vehicle.data.id}`)
            .then((response) => {
                commit(DELETE_VEHICLE);
                resolve(response);
            })
            .catch((error) => reject(error));
    });
}

export function updateVehicle({ dispatch, state }, { selectedVehicle, productId, quoteId = state?.quote?.id }) {
    return new Promise((resolve, reject) => {
        if (state?.quote?.selected_vehicle && state?.quote?.selected_vehicle?.data?.id) {
            dispatch('deleteVehicle')
                .then((response) => {
                    dispatch('createVehicle', {
                        selectedVehicle,
                        productId,
                        quoteId,
                    })
                        .then((response) => {
                            resolve(response);
                        })
                        .catch((error) => reject(error));
                })
                .catch((error) => reject(error));
        } else {
            if (state?.totals && state?.totals?.data > 0) {
                if (state?.totals?.data?.base_rental > 0) {
                    dispatch('setDevLocation', true).catch((error) => console.log(error));
                    dispatch('setMockLocation', 'US').catch((error) => console.log(error));
                }
            }
            dispatch('createVehicle', { selectedVehicle, productId, quoteId })
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => reject(error));
        }
    });
}

export function updateVehicleWithBooking({ commit, dispatch, state }, { selectedVehicle, productId, bookingId, bookingReference, quoteId = state?.quote?.id, useFreeProductUpgrade = null }) {
    return new Promise((resolve, reject) => {
        if (state?.totals && state?.totals?.data > 0) {
            if (state?.totals?.data?.base_rental > 0) {
                dispatch('setDevLocation', true).catch((error) => console.log(error));
                dispatch('setMockLocation', 'US').catch((error) => console.log(error));
            }
        }

        let quoteSelectedExtras = [];

        // Only send the selected extras for the ongoing modification. We don't want to send extras from previous modifications.
        if (state.modification?.changes?.status !== 'confirmed' && typeof state.modification?.changes?.status !== 'undefined') {
            quoteSelectedExtras = state.modification?.changes?.extras || state.modification?.extras;
        }

        dispatch('modifyBookingVehicle', {
            selectedVehicle,
            productId,
            quoteId,
            bookingId,
            bookingReference,
            useFreeProductUpgrade,
        })
            .then((response) => {
                commit(SET_GRAND_TOTAL, +response?.data?.data[0]?.grand_total);

                const productType = state.modification?.rental_code;

                return dispatch('modifyBookingExtras', {
                    quoteSelectedExtras,
                    productType,
                    quoteId,
                    bookingId,
                    bookingReference,
                })
                    .then(() => {
                        resolve(response);
                        return response;
                    })
                    .catch((error) => reject(error));
            })
            .catch((error) => reject(error));
    });
}

export function fetchModification({ commit, dispatch, state }, { bookingId }) {
    return new Promise((resolve, reject) => {
        return HTTP.get(`modify-reservation/${bookingId}/details/${state.quote?.id}`)
            .then((response) => {
                commit(FETCH_MODIFICATION, response?.data?.data);
                commit(SET_GRAND_TOTAL, +response?.data);

                resolve(response);
                return response;
            })
            .catch((error) => console.log(error));
    });
}

export function fetchLastModification({ commit, dispatch, state }, { bookingId }) {
    return new Promise((resolve, reject) => {
        return HTTP.get(`modify-reservation/${bookingId}/last-details`)
            .then((response) => {
                commit(FETCH_LAST_MODIFICATION, response?.data?.data);

                resolve(response);
                return response;
            })
            .catch((error) => console.log(error));
    });
}

export function fetchLastModificationWithVehicle({ commit, dispatch, state }, { bookingId }) {
    return new Promise((resolve, reject) => {
        return HTTP.get(`modify-reservation/${bookingId}/last-details?include=vehicle_for_price`)
            .then((response) => {
                commit(FETCH_LAST_MODIFICATION, response?.data?.data);

                resolve(response);
                return response;
            })
            .catch((error) => console.log(error));
    });
}

export function fetchModificationWithQuote({ commit, dispatch, state }, { bookingId, quoteId }) {
    return new Promise((resolve, reject) => {
        return HTTP.get(`modify-reservation/${bookingId}/details/${quoteId}`)
            .then((response) => {
                resolve(response);
                return response;
            })
            .catch((error) => console.log(error));
    });
}

export function updateExtrasWithBooking({ commit, dispatch, state }, { selected, extra, bookingId, booking_reference, quoteId = state.quote.id, modification = null }) {
    return new Promise((resolve, reject) => {
        let quoteSelectedExtras = [];

        if (typeof state.modification?.changes?.extras !== 'undefined' && state.modification?.status === 'checked') {
            for (let i = 0; i < state.modification?.changes?.extras?.length; i++) {
                if ((selected && extra.external_id !== state.modification?.changes?.extras[i]?.external_id) || !selected) {
                    // Add a new "is_old_extra" param to know that these extras are old ones from the original quote
                    quoteSelectedExtras.push({
                        ...state.modification?.changes?.extras[i],
                        is_old_extra: true,
                    });
                }
            }
        }

        // Also check if there are extras in the passed modification details
        if (typeof modification?.extras !== 'undefined' && modification?.status === 'checked') {
            for (let i = 0; i < modification?.extras?.length; i++) {
                if ((selected && extra.external_id !== modification?.extras[i]?.external_id) || !selected) {
                    // Only add the extras that are not already in the array from the previous checks.
                    // This extra addition is required when selecting extras from PMP product.
                    if (!quoteSelectedExtras.find((e) => e.id === modification?.extras[i].id)) {
                        // Add a new "is_old_extra" set to false to know that these are new extras selected during this modification
                        quoteSelectedExtras.push(modification?.extras[i]);
                    }
                }
            }
        }

        if (!selected) {
            // Add a new "is_old_extra" set to false to know that this is a new extra being added
            extra.is_old_extra = false;
            quoteSelectedExtras.push(extra);
        }

        const productType =
            typeof state.modification === 'undefined' ||
            typeof state.modification.changes === 'undefined' ||
            (typeof state.modification?.changes?.rental_code === 'string' && state.modification?.changes?.rental_code === '') ||
            typeof state.modification?.changes?.errors !== 'undefined'
                ? state.quote?.selected_vehicle?.data?.product?.data?.type
                : state.modification?.changes?.rental_code;

        dispatch('modifyBookingExtras', {
            quoteSelectedExtras,
            productType,
            quoteId,
            bookingId,
            bookingReference: booking_reference,
            shouldKeepOldExtras: false,
            extra,
            selected,
        }).then((response) => {
            resolve(response);
            return response;
        });
    });
}

export function modifyBookingExtras(
    { commit, dispatch, state },
    { quoteSelectedExtras, productType, quoteId, bookingId, bookingReference, shouldKeepOldExtras = true, extra = null, selected = null }
) {
    return new Promise((resolve, reject) => {
        return HTTP.post(`quotes/${quoteId}/modify/selected-extras/${bookingId}`, {
            extras: quoteSelectedExtras,
            product_type: productType,
            booking_reference: bookingReference,
            should_keep_old_extras: shouldKeepOldExtras,
            extra,
            selected,
        }).then((response) => {
            let extras = typeof response?.data?.data[0]?.extras !== 'undefined' ? response?.data?.data[0]?.extras : [];

            commit(SET_GRAND_TOTAL, +response?.data?.data[0]?.grand_total);
            commit(FETCH_MODIFICATION, response?.data?.data[0]);

            commit(FETCH_MODIFICATION_EXTRAS, extras);
            resolve(response);
            return response;
        });
    });
}

export function checkWebOfficeBooking({ commit, dispatch, state }, { bookingId }) {
    return new Promise((resolve, reject) => {
        return HTTP.post(`modify-reservation/${bookingId}/partial`, {
            modification: state?.modification?.changes,
        }).then((response) => {
            commit(FETCH_MODIFICATION, response?.data);
            resolve(response);
            return response;
        });
    });
}

export function cancelBookingModification({ commit, dispatch, state }, { bookingReference }) {
    return new Promise((resolve, reject) => {
        return HTTP.post(`modify-reservation/${bookingReference}/cancel-modification`)
            .then((response) => {
                commit(FETCH_MODIFICATION, response?.data);
                resolve(response);
                return response;
            })
            .catch((error) => {
                reject(error);
            });
    });
}

export function confirmWebOfficeBooking({ commit, dispatch, state }, { bookingId: bookingId, quoteId: quoteId }) {
    return new Promise((resolve, reject) => {
        return HTTP.post(`modify-reservation/${bookingId}/partial/confirm`, {
            booking_reference: bookingId,
            quote_id_hash: quoteId,
        }).then((response) => {
            commit(FETCH_MODIFICATION, response?.data?.data[0]);
            resolve(response);
            return response;
        });
    });
}

export function createExtra({ dispatch, state }, payload) {
    //TODO: How to handle multiple items?
    const choice = payload.extra.choices_type == 'boolean' ? true : 1;

    return HTTP.post(`quotes/${state.quote.id}/selected-extras`, {
        id: payload.extra.id,
        choice: choice,
        prepay: payload.prepay,
    }).then((response) => dispatch('fetchQuote', state.quote.id));
}

export function createExtraForModification({ dispatch, state }, payload) {
    const choice = payload.extra.choices_type == 'boolean' ? true : 1;

    return HTTP.post(`quotes/${payload.quote.id}/selected-extras`, {
        id: payload.extra.id,
        choice: choice,
        prepay: payload.prepay,
    }).then((response) => dispatch('fetchQuote', payload.quote.id));
}

export function createVehicleInsuranceOption({ dispatch, state }, payload) {
    return HTTP.post(`quotes/${state.quote.id}/selected-vehicles/${state.quote.selected_vehicle.data.id}/selected-insurance-options`, {
        id: payload.insuranceOption.id,
        prepay: payload.prepay,
    }).then((response) => dispatch('fetchQuote', state.quote.id));
}

export function deleteVehicleInsuranceOption({ dispatch, state }, selectedInsuranceOption) {
    return HTTP.delete(`quotes/${state.quote.id}/selected-vehicles/${state.quote.selected_vehicle.data.id}/selected-insurance-options/${selectedInsuranceOption.id}`).then((response) =>
        dispatch('fetchQuote', state.quote.id)
    );
}

export function updateVehicleInsuranceOption({ dispatch, state }, payload) {
    return HTTP.patch(`quotes/${state.quote.id}/selected-vehicles/${state.quote.selected_vehicle.data.id}/selected-insurance-options/${payload.selectedInsuranceOption.id}`, {
        prepay: payload.prepay,
    }).then((response) => dispatch('fetchQuote', state.quote.id));
}

export function deleteExtra({ dispatch, state }, selectedExtra) {
    let selectedExtraIdObjects = [];
    if (state.quote.selected_extras && state.quote.selected_extras.data) {
        selectedExtraIdObjects = state.quote.selected_extras.data.filter((extra) => {
            return extra.extra.data.id == selectedExtra.id;
        });
    }

    if (!selectedExtraIdObjects.length) {
        return Promise.reject(new Error('Selected extra not found for modification'));
    }

    const selectedExtraIdObject = selectedExtraIdObjects[0];

    return HTTP.delete(`quotes/${state.quote.id}/selected-extras/${selectedExtraIdObject.id}`).then((response) => dispatch('fetchQuote', state.quote.id));
}

export function deleteExtraForModification({ dispatch, state }, payload) {
    const selectedExtraIdObjects = state.quote.selected_extras.data.filter((extra) => {
        return extra.extra.data.id == payload.extra.id;
    });

    const selectedExtraIdObject = selectedExtraIdObjects[0];

    return HTTP.delete(`quotes/${payload.quote.id}/modification-selected-extras/${selectedExtraIdObject.id}`).then((response) => dispatch('fetchQuote', payload.quote.id));
}

export function updateExtra({ dispatch, state }, payload) {
    // TODO: Consider pattern(s) for throttling/blocking?
    let selectedExtraIdObjects = [];

    if (state.quote.selected_extras) {
        selectedExtraIdObjects = state.quote.selected_extras.data.filter((extra) => {
            return extra.extra.data.id == payload.extra.id;
        });
    }

    if (selectedExtraIdObjects.length) {
        if (payload.choice == 0) {
            dispatch('deleteExtra', payload.extra);
        } else {
            const selectedExtraIdObject = selectedExtraIdObjects[0];

            return HTTP.patch(`quotes/${state.quote.id}/selected-extras/${selectedExtraIdObject.id}`, {
                choice: payload.choice,
                prepay: payload.prepay,
            }).then((response) => dispatch('fetchQuote', state.quote.id));
        }
    } else {
        dispatch('createExtra', {
            extra: payload.extra,
            prepay: payload.prepay,
        });
    }
}

export function updateQuote({ commit, dispatch, state }, payload) {
    const quoteId = payload.quoteId ? payload.quoteId : state.quote.id;
    const hasBooking = !!state?.booking?.reference;
    const isOnlyFreeProductUpgradeToggle = Object.keys(payload).every((key) => ['quoteId', 'payment_type', 'use_free_product_upgrade'].includes(key)) && 'use_free_product_upgrade' in payload;

    if ('payment_type' in payload) {
        commit(UPDATE_PAYMENT_TYPE, payload.payment_type);
    }
    if ('green_heart_donation' in payload) {
        commit(UPDATE_DONATION, payload.green_heart_donation);
    }

    return HTTP.patch(`quotes/${quoteId}`, payload).then((response) => {
        if (isOnlyFreeProductUpgradeToggle) {
            const requests = [
                dispatch('fetchQuoteWithAdditionalIncludes', {
                    quoteId,
                    additionalIncludes: 'selected_vehicle.basic_vehicle,selected_vehicle.premium_vehicle',
                }),
                dispatch('fetchVehicles', {
                    quoteId,
                    sortDriveAndGo: '&sort_drive_and_go',
                }),
            ];

            if (hasBooking) {
                return Promise.all(requests).then(() => response);
            }

            return response;
        }

        const requests = [
            dispatch('fetchQuote', state?.quote?.id ? state.quote.id : quoteId),
            dispatch('fetchVehicles', {
                quoteId: state.quote.id,
                sortDriveAndGo: '&sort_drive_and_go',
            }),
            dispatch('fetchExtras', state.quote.id),
        ];

        if (hasBooking) {
            return Promise.all(requests).then(() => response);
        }

        return response;
    });
}

export function updateQuoteLocale({ dispatch, state }, payload) {
    return HTTP.patch(`quotes/${state.quote.id}/locale`, payload).then((response) => dispatch('fetchQuote', state.quote?.id));
}

export function fetchTotalsWithoutCommit({ commit }, quoteId) {
    return HTTP.get(`quotes/${quoteId}/totals`).then((response) => {
        return response;
    });
}

export function fetchTotals({ commit }, quoteId) {
    return new Promise((resolve, reject) => {
        return HTTP.get(`quotes/${quoteId}/totals`).then((response) => {
            commit(FETCH_TOTALS, response?.data?.data);
            commit(SET_GRAND_TOTAL, response?.data?.data?.total);
            resolve(response);
        });
    });
}

export function fetchDonations({ commit }, sourceId) {
    return HTTP.get(`sources/${sourceId}/donation-types`).then((response) => commit(FETCH_DONATIONS, response.data.data));
}

export function fetchLoyaltyLevels({ commit }) {
    return HTTP.get(`loyalty-levels`).then((response) => {
        commit(FETCH_LOYALTY_LEVELS, response.data.data);
        return response;
    });
}

export function setCustomerState({ commit }, payload) {
    commit(SET_CUSTOMER_STATE, payload);
}

export function updateBookingQuotesCurrency({ commit, dispatch, state }, { bookingReference, currencyFrom, currencyTo }) {
    return new Promise((resolve, reject) => {
        return HTTP.post(`modify-reservation/${bookingReference}/currency`, {
            currencyFrom,
            currencyTo,
        }).then((response) => {
            resolve(response);
            return response;
        });
    });
}

export function setCurrencyState({ commit, dispatch, state }, currency) {
    // Re-selecting the currency that's already active is a no-op: several components
    // (Vehicles.vue's initial currency sync, Layout.vue's Kayak init) can each
    // independently call this with the same currency, and without this guard every one
    // of those redundant calls would re-post to the API and re-trigger the vehicles/quote
    // refetch in applyCurrencyChange, racing duplicate requests that clobber the loaded
    // vehicle list.
    if (currency && state.currency && currency.code === state.currency.code) {
        return Promise.resolve();
    }

    // A call for the same currency is already in flight - piggyback on it rather than
    // starting a second, parallel currency-change cycle.
    if (currency && pendingCurrencyChange && pendingCurrencyChange.code === currency.code) {
        return pendingCurrencyChange.promise;
    }

    const promise = new Promise((resolve, reject) => {
        let bookingReference = null;
        const cookie = JSON.parse(localStorage.getItem('booking'));

        if (cookie) {
            bookingReference = cookie.bookingReference;
        }

        if (typeof currency !== 'undefined') {
            const currencyTo = typeof currency !== 'undefined' ? currency.code : null;

            if (currencyTo) {
                localStorage.setItem('currency', currencyTo);

                if (bookingReference) {
                    let currencyFrom = state.currency || window.greenmotion.defaultCurrency;
                    currencyFrom = currencyFrom.code;
                    dispatch('updateBookingQuotesCurrency', {
                        bookingReference,
                        currencyFrom,
                        currencyTo,
                    });
                }
                resolve(currencyTo);
            }
        } else {
            resolve();
        }
    }).then(() => {
        dispatch('applyCurrencyChange', currency);

        // Other app bundles on the page (e.g. the header) hold their own store instance -
        // see currencySync.js.
        if (currency) {
            broadcastCurrencyChange(currency);
        }
    });

    if (currency) {
        pendingCurrencyChange = { code: currency.code, promise };
        promise.finally(() => {
            if (pendingCurrencyChange && pendingCurrencyChange.code === currency.code) {
                pendingCurrencyChange = null;
            }
        }).catch(() => {});
    }

    return promise;
}

// The commit-and-refetch half of a currency change, without setCurrencyState's one-off
// side effects (localStorage write, booking currency POST). setCurrencyState runs it for
// its own store; currencySyncPlugin runs it for every other store on the page.
export function applyCurrencyChange({ commit, dispatch, state }, currency) {
    // Covers the originating store receiving its own broadcast, as well as repeat calls.
    if (currency && state.currency && currency.code === state.currency.code) {
        return;
    }

    if (currency) {
        commit(SET_CURRENCY, currency);
    }

    // Notify mounted listings even before quote hydration: they can refetch using
    // their route ID and invalidate responses requested in the previous currency.
    commit(SET_CURRENCY_CHANGE_TOKEN, state.currencyChangeToken + 1);

    if (!state.quote.id) {
        return;
    }

    commit(SET_CURRENCY_REFRESHING, true);

    // Absorbs what used to be Globals.fetchVehiclesOnCurrencyChange, dispatched
    // independently by every component that needed fresh prices after a currency
    // change - owning it here means there's one place to fix instead of N.
    //
    // Deliberately not awaited as part of setCurrencyState's own promise: selectCurrency()
    // chains window.bus.$emit('currency.changed', ...) onto that promise today to reset
    // every price card's "currencyIsChanging" placeholder, and that needs to keep
    // firing on its current (near-immediate) timing, not stall for this refetch.
    const reportError = (error) => dispatch('createError', error);

    const requests = [dispatch('fetchQuote', state.quote.id).catch(reportError)];

    // When a Vehicles.vue listing is mounted it refetches the list itself off the
    // currencyChangeToken bump above, with its own filters, sort and loaded-page limit.
    // Fetching a generic list here too would briefly replace that view with the full
    // unfiltered list (and leave it there if the listing's own request then failed).
    if (!state.vehicleListingOwners) {
        requests.push(dispatch('fetchVehicles', { quoteId: state.quote.id }).catch(reportError));
    }

    if (state.quote.selected_vehicle && state.quote.selected_vehicle.data.id) {
        requests.push(
            dispatch('fetchVehicleInsuranceOptions', {
                quoteId: state.quote.id,
                vehicleId: state.quote.selected_vehicle.data.vehicle.data.id,
            }).catch(reportError),
            dispatch('fetchExtras', state.quote.id).catch(reportError),
        );
    }

    // Only clear if no newer currency change has started in the meantime.
    const token = state.currencyChangeToken;
    Promise.all(requests).then(() => {
        if (state.currencyChangeToken === token) {
            commit(SET_CURRENCY_REFRESHING, false);
        }
    });

}

export function registerVehicleListing({ commit }) {
    commit(REGISTER_VEHICLE_LISTING);
}

export function unregisterVehicleListing({ commit }) {
    commit(UNREGISTER_VEHICLE_LISTING);
}

export function setDefaultCurrency({ commit, dispatch, state }) {
    new Promise((resolve, reject) => {
        resolve(localStorage.getItem('currency'));
    }).then((storedCurrency) => {
        storedCurrency = storedCurrency || window.greenmotion.defaultCurrency;

        const selectedCurrency = state.currencies.filter((currency) => {
            return currency.code == storedCurrency;
        });

        if (selectedCurrency) {
            dispatch('setCurrencyState', selectedCurrency[0]);
        }
    });
}

export function setPickupDate({ commit }, date) {
    commit(SET_PICKUP_DATE, date);
}

export function setPickupTime({ commit }, time) {
    commit(SET_PICKUP_TIME, time);
}

export function setDropoffDate({ commit }, date) {
    commit(SET_DROPOFF_DATE, date);
}

export function setDropoffTime({ commit }, time) {
    commit(SET_DROPOFF_TIME, time);
}

export function setPickupLocation({ commit }, location) {
    commit(SET_PICKUP_LOCATION, location);
}

export function setDropoffLocation({ commit }, location) {
    commit(SET_DROPOFF_LOCATION, location);
}

export function linkQuoteUser({ commit }, { userId, quoteId }) {
    return HTTP.post(`${localDomain}quotes/${quoteId}/link`, {
        user_id: userId,
    });
}

export function createBooking({ commit }, payload) {
    return HTTP.post('bookings', payload);
}

export function togglePremiumVehicleUpgrade({ commit, dispatch, state }) {
    const selectedVehicle = state.hasUpgradedVehicle ? state.quote.selected_vehicle.data.basic_vehicle.data : state.quote.selected_vehicle.data.premium_vehicle.data;
    dispatch('updateVehicle', { selectedVehicle });
    commit(TOGGLE_PREMIUM_VEHICLE_UPGRADE);
}

export function setSelectedVehicleType({ commit }, type) {
    commit(SET_SELECTED_VEHICLE_TYPE, type);
}

export function intendedRoute({ dispatch, state }, { route }) {
    return new Promise((resolve, reject) => {
        return BOOKINGHTTP.post(`user/create/intended`, {
            intended_url: route,
        }).then((response) => {
            resolve(response);
            return response;
        });
    });
}

export function setTab({ commit }, tab) {
    commit(SET_TAB, tab);
}
